import { Helmet } from 'react-helmet'

interface OpenGraphMetaProps {
  title: string
  description: string
  url: string
  image?: string
  type?: 'website' | 'article'
  author?: string
  publishedTime?: string
  tags?: string[]
}

export default function OpenGraphMeta({
  title,
  description,
  url,
  image = 'https://gotocoin.au/og-default.png',
  type = 'article',
  author = 'GoToCoins',
  publishedTime,
  tags = []
}: OpenGraphMetaProps) {
  const fullUrl = url.startsWith('http') ? url : `https://gotocoin.au${url}`

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="GoToCoins" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Article specific */}
      {type === 'article' && (
        <meta property="article:author" content={author} />
      )}
      {type === 'article' && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === 'article' && tags.map((tag, index) => (
        <meta key={index} property="article:tag" content={tag} />
      ))}

      {/* LinkedIn specific */}
      <meta property="og:locale" content="en_AU" />

      {/* Canonical */}
      <link rel="canonical" href={fullUrl} />

      {/* Article structured data */}
      {type === 'article' && (
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: title,
            description,
            image,
            datePublished: publishedTime,
            author: { '@type': 'Organization', name: author },
            publisher: {
              '@type': 'Organization',
              name: 'GoToCoins',
              logo: { '@type': 'ImageObject', url: 'https://gotocoin.sydneygotoman.com/pwa-512x512.png' }
            },
            mainEntityOfPage: fullUrl,
            keywords: tags.join(', ')
          })}
        </script>
      )}
    </Helmet>
  )
}
